import { AUDIENCES, T, type AudienceKey } from './copy'
import { GhostButton, ThresholdCanvas } from './primitives'

interface AccessDeniedScreenProps {
  audience: AudienceKey
  onHome: () => void
  onSignOut: () => void
  testId?: string
}

/**
 * Screen 3 — the access-denied threshold shown when a user is authenticated
 * but lacks the required role. A single treatment reused across admin and
 * consultant, parameterized only by copy; framed as grace, not an error dump.
 *
 * @param audience - Which role audience's copy to show ('admin' | 'consultant').
 * @param onHome - Invoked by "Return home" (navigates to the public site).
 * @param onSignOut - Invoked by "Sign out" (wired to MSAL logout()).
 * @param testId - data-testid applied to the stage root. Defaults to 'access-denied'.
 * @returns The access-denied threshold screen.
 */
export function AccessDeniedScreen({
  audience,
  onHome,
  onSignOut,
  testId = 'access-denied',
}: AccessDeniedScreenProps) {
  const a = AUDIENCES[audience]
  return (
    <ThresholdCanvas testId={testId}>
      <div style={{ maxWidth: '24ch' }}>
        <p className="rise" style={{ ...T.eyebrow, animationDelay: '300ms' }}>
          Private
        </p>
        <h1
          className="rise"
          style={{ ...T.display, fontSize: 'clamp(2.4rem, 6vw, 4.6rem)', marginTop: 22, animationDelay: '450ms' }}
        >
          This space isn’t available to you.
        </h1>
        <p className="rise" style={{ ...T.body, marginTop: 26, maxWidth: '46ch', animationDelay: '700ms' }}>
          {a.denied}
        </p>
        <div
          className="rise"
          style={{ display: 'flex', gap: 14, marginTop: 44, flexWrap: 'wrap', animationDelay: '950ms' }}
        >
          <GhostButton onClick={onHome}>Return home</GhostButton>
          <GhostButton onClick={onSignOut}>Sign out</GhostButton>
        </div>
      </div>
    </ThresholdCanvas>
  )
}
